import { Button } from '@ahafood/ui/components/button';
import { Card } from '@ahafood/ui/components/card';
import { formatCurrency } from '@ahm/common-helpers';
import { useQueryClient } from '@tanstack/react-query';
import { useParams } from 'next/navigation';
import { useEffect, useState, useMemo, useCallback } from 'react';
import { toast } from 'sonner';
import { useWebstoreSession } from '../../../components/auth/webstore-session-provider';
import type { Store } from '@/api/ahafood/get-store-fn';
import type { EstimateItemFeeResponse } from '@/api/use-estimate-item-fee';
import {
  useEstimateItemFeeWS,
  type EstimateItemFeeParams as EstimateItemFeeParamsWS,
} from '@/api/use-estimate-item-ws';
import { useNavigation } from '@/components/navigation/navigation-context';
import { ROUTES } from '@/config/router';
import { useBottomPosition } from '@/hooks/use-bottom-position';
import { useRouter } from '@/i18n/routing';
import { getAuthToken } from '@/lib/auth/get-token';
import { calculateETA } from '@/lib/eta-calculator';
import { trackEvent } from '@/lib/event-tracking';
import { useAuthStore } from '@/store';
import { useCartStore } from '@/store/cart/cart-store-provider';

type Props = {
  store?: Store;
};

export function CartFooter({ store }: Props) {
  const router = useRouter();
  const { user } = useAuthStore();
  const branchId = useCartStore((state) => state.branchId);
  const storeSlug = useCartStore((state) => state.storeSlug);
  const currentStoreId = useCartStore((state) => state.currentStoreId);
  const { storeId } = useParams<{ storeId: string }>();
  const items = useCartStore((state) => state.items);
  const { isNavigationVisible } = useNavigation();
  const setEstimatedItem = useCartStore((state) => state.setEstimatedItem);
  const { storeData } = useWebstoreSession();
  const [totalItemsEstimatedFee, setTotalItemsEstimatedFee] = useState(0);
  const [estimateData, setEstimateData] =
    useState<EstimateItemFeeResponse | null>(null);
  const [isStoreClosed, setIsStoreClosed] = useState(false);
  const token = getAuthToken();
  const tokenStr = typeof token === 'string' ? token : '';
  const queryClient = useQueryClient();
  const { bottomPosition } = useBottomPosition();

  function etaMinutes(cityId: string | undefined, distance: number) {
    return calculateETA(cityId ?? null, distance);
  }
  const handleEstimateSuccess = useCallback(
    (data: EstimateItemFeeResponse) => {
      queryClient.setQueryData<EstimateItemFeeResponse>(
        ['estimateItemFee'],
        data
      );
      setEstimatedItem(data.total_payment);
      setTotalItemsEstimatedFee(data.total_payment);
      setEstimateData(data); // Lưu data để sử dụng trong tracking
      setIsStoreClosed(false);
      // if (mode === 'webview') {
      //   try {
      //     saveSessionBeforeExit();
      //   } catch (error) {
      //     console.error('Failed to save session after estimate:', error);
      //   }
      // }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  const handleEstimateError = useCallback((error: any) => {
    if (error?.response?.data.code === 'STORE_CLOSED') {
      toast.error(error.response.data.title);
      setIsStoreClosed(true);
    }
    if (error?.response?.data.title === 'BALANCE_SUSPENDED') {
      toast.error('Cửa hàng tạm ngưng');
    } else {
      toast.error('Món đã hết hoặc ngưng bán. Vui lòng chọn món khác.');
    }
    setTotalItemsEstimatedFee(0);
  }, []);

  // Mutations for REST and WS
  const { mutate: estimateItemFeeWs, isPending: isEstimateLoadingWs } =
    useEstimateItemFeeWS(tokenStr, {
      onSuccess: handleEstimateSuccess,
      onError: handleEstimateError,
    });

  const itemsArray = useMemo(() => Object.values(items), [items]);
  const hasItems = itemsArray.length > 0;

  const totalQuantity = useMemo(
    () => itemsArray.reduce((sum, item) => sum + (item.quantity || 1), 0),
    [itemsArray]
  );

  // Debug effect for cart footer items (optional - remove in production)
  // useEffect(() => {
  //   console.log('Cart Footer - Items changed:', {
  //     itemsCount: itemsArray.length,
  //     hasItems,
  //     mode,
  //     storeId: storeData?._id,
  //   });
  // }, [itemsArray.length, hasItems, mode, storeData?._id]);

  // Memoize store ID để tránh re-calculation không cần thiết
  const storeIdToUse = useMemo(() => {
    // Ưu tiên sử dụng currentStoreId từ cart store
    // Fallback về storeData._id, sau đó là storeId từ URL params
    return currentStoreId
      ? Number(currentStoreId)
      : (storeData?._id ?? Number(storeId));
  }, [currentStoreId, storeData?._id, storeId]);

  // Memoize items data để tránh trigger estimate khi object reference thay đổi
  const itemsData = useMemo(() => {
    return itemsArray.map((item) => ({
      id: Number(item._id),
      quantity: item.quantity || 1,
      note: item.note,
      customizations:
        item.customizations?.map((custom) => ({
          id: custom.selectedOption?._id ?? '',
          quantity: 1,
        })) ?? [],
    }));
  }, [itemsArray]);

  const itemsCount = itemsArray.length;

  // Handle cart button click với tracking
  const handleCartClick = useCallback(() => {
    // Get the first tag from cart items (prioritize first found tag)
    const cartTag = itemsArray.find((item) => (item as any).tag)?.tag;

    // Create order event data với individual items (giống PaymentFooter)
    const orderEventData: Record<string, any> = {
      store_id: storeIdToUse,
      address: store?.address,
      distance: store?.distance,
      store_slug: storeSlug,
      total_quantity: totalQuantity,
      total_amount: totalItemsEstimatedFee,
      button_name: 'cart_button',
      eta: etaMinutes(store?.city_id, store?.distance ?? 0),
      user_id: user?.cunstomerId,

      // Thêm thông tin từ estimateData
      delivery_fee: estimateData?.delivery_fee,
      platform_fee: estimateData?.platform_fee,
      item_fee: estimateData?.item_fee,
      total_payment: estimateData?.total_payment,
      subtotal: estimateData?.subtotal,
      delivery_discount: estimateData?.delivery_discount,
      merchant_discount: estimateData?.merchant_discount,
      platform_discount: estimateData?.platform_discount,
      item_discount: estimateData?.item_discount,
      estimated_total_payment: estimateData?.estimated_total_payment,
      ...(cartTag && { tag: cartTag }),
    };

    // Add individual items as separate fields (up to 10 items to avoid too many fields)
    itemsArray.slice(0, 10).forEach((item, index) => {
      const itemPosition = index + 1;
      orderEventData[`item_${itemPosition}`] = {
        id: item._id,
        name: item.name || `Item ${itemPosition}`,
        quantity: item.quantity || 1,
        price: item.price || 0,
        position: itemPosition,
      };
    });

    // Add total items count if more than 10
    if (itemsArray.length > 10) {
      orderEventData.total_items_count = itemsArray.length;
      orderEventData.additional_items = itemsArray.length - 10;
    }

    // Track cart button click event
    trackEvent('order_event', {
      action_time: Math.floor(Date.now() / 1000),
      ...orderEventData,
    });

    router.push(ROUTES.orderCart(storeId ?? branchId));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    storeIdToUse,
    storeSlug,
    totalQuantity,
    totalItemsEstimatedFee,
    estimateData,
    router,
    storeId,
    branchId,
  ]);

  // Debounce estimate API trong useEffect với cleanup CLEAR TIMER trước mỗi
  // re-run → tránh duplicate calls do Strict Mode double-effect / deps churn.
  useEffect(() => {
    if (itemsCount === 0) {
      setTotalItemsEstimatedFee(0);
      setEstimatedItem(0);
      setIsStoreClosed(false);
      return;
    }

    const timer = setTimeout(() => {
      const dataWS: EstimateItemFeeParamsWS = {
        receiver: {},
        items: itemsData,
        store_id: storeIdToUse,
      };
      estimateItemFeeWs(dataWS);
    }, 300);

    return () => clearTimeout(timer);
  }, [
    itemsCount,
    itemsData,
    storeIdToUse,
    estimateItemFeeWs,
    setEstimatedItem,
  ]);

  return (
    <>
      {hasItems && (
        <>
          {/* Mobile cart footer */}
          <div className="h-16 md:hidden"></div>
          <div
            className="fixed inset-x-0 z-20 w-full max-w-lg mx-auto md:hidden"
            style={{
              bottom: isNavigationVisible ? bottomPosition : 0,
            }}
          >
            <Card className="border-0 shadow-none rounded-t-lg mx-2 p-4 bg-white">
              {isStoreClosed ? (
                <div className="w-full py-3 px-6 text-center font-bold text-red-50">
                  Quán đã đóng cửa
                </div>
              ) : (
                <Button
                  size="lg"
                  disabled={isEstimateLoadingWs}
                  className="w-full flex justify-between items-center gap-4 px-6 font-bold"
                  onClick={handleCartClick}
                >
                  <span>Giỏ hàng: {totalQuantity} món</span>
                  <span>
                    {isEstimateLoadingWs ? (
                      <span className="text-white font-normal animate-pulse">
                        Đang tính...
                      </span>
                    ) : (
                      formatCurrency(totalItemsEstimatedFee)
                    )}
                  </span>
                </Button>
              )}
            </Card>
          </div>

          {/* Desktop cart summary */}
          <div className="hidden md:block">
            <Card className="border shadow-sm rounded-lg p-6">
              <div className="space-y-4">
                <div className="flex justify-between items-center">
                  <h3 className="text-lg font-semibold">Tóm tắt đơn hàng</h3>
                  <span className="text-sm text-gray-500">
                    {totalQuantity} món
                  </span>
                </div>

                <div className="space-y-2">
                  <div className="flex justify-between text-sm">
                    <span>Tạm tính:</span>
                    <span>
                      {isEstimateLoadingWs ? (
                        <span className="animate-pulse">Đang tính...</span>
                      ) : (
                        formatCurrency(totalItemsEstimatedFee)
                      )}
                    </span>
                  </div>
                </div>

                <div className="border-t pt-4">
                  <div className="flex justify-between font-semibold text-lg">
                    <span>Tổng cộng:</span>
                    <span className="text-primary">
                      {isEstimateLoadingWs ? (
                        <span className="animate-pulse">Đang tính...</span>
                      ) : (
                        formatCurrency(totalItemsEstimatedFee)
                      )}
                    </span>
                  </div>
                </div>

                {isStoreClosed ? (
                  <div className="w-full py-3 px-6 text-center font-bold text-red-60 rounded-lg border border-red-50">
                    Quán đã đóng cửa
                  </div>
                ) : (
                  <Button
                    size="lg"
                    disabled={isEstimateLoadingWs}
                    className="w-full font-bold"
                    onClick={handleCartClick}
                  >
                    Tiếp tục thanh toán
                  </Button>
                )}
              </div>
            </Card>
          </div>
        </>
      )}
    </>
  );
}
